// Reporte de visita tecnica (formato del cliente, ej. "Reporte de visita - INSURGENTES"):
// - Hoja 1: carta dirigida al cliente (asunto, lugar y fecha, destinatario, objetivo de la visita,
//   resumen de lo atendido, detalle de preventivos y de reportes, cierre y firma).
// - Anexo A: banda con el codigo del formato y dos tablas: Servicios realizados y Reportes atendidos.
// Se descarga desde cualquier servicio de la visita (mismo cliente / sucursal / dia).
// datos: respuesta de cmms/datosReporteVisitaPdfCMMS. textos: los de la carta (editables en el
// modal antes de generar); sin ellos se usan los de textosReporteVisita(datos).
import { AZUL, GRIS, partible, documentoCMMS, lineaFirma } from "./comunCMMSPdf";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

// "YYYY-MM-DD" (o Date) -> "jueves 8 de octubre del 2026"
export const fechaLarga = (f) => {
    if (!f) return "";
    const s = typeof f === "string" ? f.slice(0, 10) : `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}`;
    const [a, m, d] = s.split("-").map(Number);
    if (!a || !m || !d) return "";
    const dia = new Date(Date.UTC(a, m - 1, d)).getUTCDay();
    return `${DIAS[dia]} ${d} de ${MESES[m - 1]} del ${a}`;
};

// "YYYY-MM-DD" -> "8/10/26"
const fechaCorta = (f) => {
    const [a, m, d] = String(f || "").slice(0, 10).split("-");
    return a && m && d ? `${Number(d)}/${Number(m)}/${a.slice(2)}` : "";
};

const UNIDADES = ["cero", "un", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve", "veinte"];
const VEINTIS = ["", "veintiún", "veintidós", "veintitrés", "veinticuatro", "veinticinco", "veintiséis", "veintisiete", "veintiocho", "veintinueve"];
const DECENAS = ["", "", "", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
const CENTENAS = ["", "ciento", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];

// Numero en letras para contar cosas ("un equipo", "veintiún equipos", "ciento dos reportes"). 0-999999.
export const numeroALetras = (n) => {
    n = Math.floor(Number(n) || 0);
    if (n <= 20) return UNIDADES[n];
    if (n < 30) return VEINTIS[n - 20];
    if (n < 100) return DECENAS[Math.floor(n / 10)] + (n % 10 ? ` y ${UNIDADES[n % 10]}` : "");
    if (n === 100) return "cien";
    if (n < 1000) return CENTENAS[Math.floor(n / 100)] + (n % 100 ? ` ${numeroALetras(n % 100)}` : "");
    if (n < 1000000) {
        const miles = Math.floor(n / 1000);
        return (miles === 1 ? "mil" : `${numeroALetras(miles)} mil`) + (n % 1000 ? ` ${numeroALetras(n % 1000)}` : "");
    }
    return String(n);
};

const contar = (n, singular, plural) => `${numeroALetras(n)} ${n === 1 ? singular : plural}`;

// "A", "A y B", "A, B e Ignacio" (e antes de i / hi).
const unirNombres = (lista) => {
    const l = (lista || []).filter(Boolean);
    if (l.length <= 1) return l[0] || "";
    const ultimo = l[l.length - 1];
    const conector = /^h?i(?![aeiouáéíóú])/i.test(ultimo) ? " e " : " y ";
    return l.slice(0, -1).join(", ") + conector + ultimo;
};

// Primera letra en minuscula salvo siglas ("Desfibrilador" -> "desfibrilador", "ECG" se queda).
const minuscula = (t) => (t && /^[A-ZÁÉÍÓÚÑ][a-záéíóúñü]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t || "");

const equipoTexto = (r) =>
    [minuscula(r.equipo) || "equipo", r.marca ? `de la marca ${r.marca}` : "", r.modelo ? `modelo ${r.modelo}` : "", r.serie ? `número de serie ${r.serie}` : ""].filter(Boolean).join(", ").replace(", de la marca", " de la marca");

const sinPunto = (t) => String(t || "").trim().replace(/[.\s]+$/, "");

// Nombre del tipo de servicio para la carta ("mantenimiento preventivo programado", "instalación").
const nombreTipo = (r, conContrato) => {
    if (r.tipoServicio === 1) return `mantenimiento preventivo${conContrato ? " programado" : ""}`;
    return minuscula(r.tipoServicioNombre) || "servicio";
};

// Mas de este numero de equipos por tipo: en la carta solo se detallan los que tienen observaciones.
const MAX_DETALLE = 8;

// Textos de la carta armados con lo que se hizo en la visita. El usuario los puede editar.
export const textosReporteVisita = (datos, hoy = new Date()) => {
    const c = datos.cliente || {};
    const imp = datos.impresion || {};
    const renglones = datos.renglones || [];
    const realizados = renglones.filter((r) => r.tabla === 0);
    const reportes = renglones.filter((r) => r.tabla === 1);
    const conContrato = renglones.some((r) => r.conContrato);
    const contratos = (datos.contratos || []).join(", ");
    const ingenieros = datos.ingenieros || [];

    // Objetivo
    const objetivo = `Por medio de la presente, se detalla la visita a la unidad realizada el ${fechaLarga(datos.fecha)} con el objetivo de realizar el mantenimiento de los equipos médicos${
        contratos ? ` bajo el contrato ${contratos}` : ""
    } para asegurar su correcto funcionamiento y detectar posibles anomalías.`;

    // Resumen: "...atendieron un total de 6 equipos; cuatro equipos para mantenimiento preventivo programado y dos reportes."
    const porTipo = new Map();
    realizados.forEach((r) => {
        const k = nombreTipo(r, conContrato);
        porTipo.set(k, [...(porTipo.get(k) || []), r]);
    });
    const partes = [...porTipo.entries()].map(([tipo, lista]) => `${contar(lista.length, "equipo", "equipos")} para ${tipo}`);
    if (reportes.length) partes.push(contar(reportes.length, "reporte", "reportes"));
    const total = renglones.length;
    const quien = !ingenieros.length
        ? total === 1
            ? "se atendió"
            : "se atendieron"
        : ingenieros.length === 1
        ? `el ingeniero ${ingenieros[0]}, atendió`
        : `los ingenieros ${unirNombres(ingenieros)}, atendieron`;
    const resumen = `Durante la visita ${quien} un total de ${total} ${total === 1 ? "equipo" : "equipos"}${partes.length ? `; ${unirNombres(partes)}` : ""}.`;

    // Detalle de los servicios realizados, un parrafo por tipo.
    const detalleRealizados = [...porTipo.entries()]
        .map(([tipo, lista]) => {
            const conObs = lista.filter((r) => r.notas || r.refacciones);
            const mostrar = lista.length > MAX_DETALLE ? conObs : lista;
            const item = (r) => {
                const obs = [sinPunto(r.notas), r.refacciones ? `se usó ${sinPunto(r.refacciones)}` : ""].filter(Boolean).join("; ");
                return `${equipoTexto(r)}${obs ? `, ${minuscula(obs)}` : ""}`;
            };
            if (!mostrar.length) return `Para ${tipo} se atendieron ${contar(lista.length, "equipo", "equipos")}, sin observaciones (ver Anexo A).`;
            const encabezado =
                lista.length > MAX_DETALLE
                    ? `Para ${tipo} se atendieron ${contar(lista.length, "equipo", "equipos")} (ver Anexo A); con observaciones se tiene${mostrar.length === 1 ? "" : "n"}: `
                    : `Para ${tipo}, se tiene${mostrar.length === 1 ? "" : "n"}: `;
            return `${encabezado}${mostrar.map(item).join("; ")}.`;
        })
        .join("\n\n");

    // Reportes atendidos.
    const detalleReportes = reportes
        .map((r) => {
            const falla = sinPunto(r.fallaReportada);
            const hecho = sinPunto(r.comentarios);
            return `Se atiende el reporte del equipo ${equipoTexto(r)}${falla ? ` por ${minuscula(falla)}` : ""}${hecho ? `; ${minuscula(hecho)}` : ""}.`;
        })
        .join(" ");

    let nombreFirmante = "";
    let puestoFirmante = "";
    try {
        const guardado = JSON.parse(window.localStorage.getItem("cmms.reporteVisita.firmante") || "null");
        nombreFirmante = guardado?.nombre || "";
        puestoFirmante = guardado?.puesto || "";
    } catch (e) {
        // sin almacenamiento local: se usan los de la visita
    }

    return {
        asunto: "Reporte De Visita Técnica",
        // "San Luis Potosí, S.L.P. a viernes 9 de octubre del 2026"; sin lugar configurado, solo la fecha.
        lugarFecha: imp.lugarEmision ? `${imp.lugarEmision} a ${fechaLarga(hoy)}` : fechaLarga(hoy).replace(/^./, (c) => c.toUpperCase()),
        destinatario: [c.nombre, c.sucursal].filter(Boolean).join("\n"),
        saludo: "Reciba un cordial saludo.",
        objetivo,
        resumen,
        detalleRealizados,
        detalleReportes,
        cierre: "Este informe se elabora con el fin de documentar las actividades realizadas durante la visita y dar seguimiento a los equipos que requieren atención.",
        despedida: "Sin más por el momento quedo de usted.",
        firmante: nombreFirmante || datos.firmante || ingenieros[0] || "",
        puesto: puestoFirmante || datos.empresa?.razonSocial || datos.empresa?.nombre || "",
        tituloAnexo: `Control de clientes${conContrato ? " con contrato" : ""}: ${c.sucursal || c.nombre || ""}`,
        departamento: "Departamento de Servicios",
    };
};

// ─── Anexo A ─────────────────────────────────────────────────────────────────

const AZUL_OSCURO = "#1b365d";

// Status del equipo como en su formato: Funcional (verde) · Atención (ámbar) · Dañado (rojo) · Baja.
const STATUS = {
    1: { texto: "Funcional", color: "#2e7d32" },
    2: { texto: "Atención", color: "#d68910" },
    3: { texto: "Dañado", color: "#c62828" },
    4: { texto: "Baja", color: "#5b6b7c" },
};

const celdaStatus = (r) => {
    const s = STATUS[r.estatusEquipo];
    return s ? { text: s.texto, bold: true, color: "#ffffff", fillColor: s.color, alignment: "center" } : { text: r.estatus || "", alignment: "center", color: GRIS };
};

const encabezado = (t) => ({ text: t, bold: true, color: "#ffffff", fillColor: AZUL_OSCURO, alignment: "center", fontSize: 6.5 });

const bordesAnexo = {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => "#b8c2cc",
    vLineColor: () => "#b8c2cc",
    paddingLeft: () => 3,
    paddingRight: () => 3,
    paddingTop: () => 2.5,
    paddingBottom: () => 2.5,
};

const tablaAnexo = (titulo, renglones, ultimaColumna, valorUltima) => [
    { text: `${titulo} (${renglones.length})`, bold: true, color: AZUL_OSCURO, fontSize: 9.5, margin: [0, 10, 0, 3] },
    {
        table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [12, 56, 40, 40, 46, 40, 34, "*", 40, 46],
            body: [
                ["No.", "Equipo", "Marca", "Modelo", "Serie", "Área", "O.S.", ultimaColumna, "Status", "Tipo de servicio"].map(encabezado),
                ...renglones.map((r, i) => [
                    { text: String(i + 1), alignment: "center", color: GRIS },
                    partible(r.equipo),
                    partible(r.marca),
                    partible(r.modelo),
                    partible(r.serie),
                    partible(r.area),
                    partible(r.os),
                    partible(valorUltima(r)),
                    celdaStatus(r),
                    partible(r.tipoServicioNombre),
                ]),
            ],
        },
        layout: bordesAnexo,
        fontSize: 6.5,
    },
];

const anexoA = (datos, t) => {
    const imp = datos.impresion || {};
    const renglones = datos.renglones || [];
    const realizados = renglones.filter((r) => r.tabla === 0);
    const reportes = renglones.filter((r) => r.tabla === 1);
    const blanco = { color: "#ffffff", fontSize: 7.5 };
    const contenido = [
        { text: "Anexo A", bold: true, fontSize: 12, color: AZUL, pageBreak: "before", margin: [0, 0, 0, 6] },
        {
            table: {
                widths: [110, "*", 110],
                body: [
                    [
                        { text: imp.codigoReporteVisita ? `Código: ${imp.codigoReporteVisita}` : "", ...blanco, bold: true, margin: [0, 6, 0, 0] },
                        {
                            stack: [
                                { text: partible(t.tituloAnexo || ""), bold: true, fontSize: 9.5, alignment: "center", color: "#ffffff" },
                                { text: t.departamento || "", alignment: "center", ...blanco },
                            ],
                        },
                        { text: `Reporte de visita: ${fechaCorta(datos.fecha)}`, ...blanco, bold: true, alignment: "right", margin: [0, 6, 0, 0] },
                    ],
                ],
            },
            layout: { hLineWidth: () => 0, vLineWidth: () => 0, fillColor: () => AZUL_OSCURO, paddingLeft: () => 8, paddingRight: () => 8, paddingTop: () => 6, paddingBottom: () => 6 },
        },
    ];
    if (realizados.length) contenido.push(...tablaAnexo("Servicios realizados", realizados, "Accesorios / Refacciones", (r) => r.refacciones));
    if (reportes.length) contenido.push(...tablaAnexo("Reportes atendidos", reportes, "Comentarios", (r) => r.comentarios));
    if (!renglones.length) contenido.push({ text: "Sin servicios en la visita.", italics: true, color: GRIS, margin: [0, 10, 0, 0] });
    return contenido;
};

// ─── Carta ───────────────────────────────────────────────────────────────────

const parrafo = (texto, extra = {}) =>
    String(texto || "")
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => ({ text: partible(p), alignment: "justify", lineHeight: 1.25, margin: [0, 0, 0, 9], ...extra }));

const carta = (t) => {
    const destinatario = String(t.destinatario || "").split("\n").filter((x) => x.trim());
    return [
        { text: [{ text: "Asunto: ", bold: true }, { text: t.asunto || "", bold: true }], alignment: "right", fontSize: 10.5, margin: [0, 4, 0, 6] },
        { text: t.lugarFecha || "", alignment: "right", fontSize: 10.5, margin: [0, 0, 0, 22] },
        ...destinatario.map((d, i) => ({ text: partible(d), bold: true, fontSize: 10.5, margin: [0, 0, 0, i === destinatario.length - 1 ? 2 : 0] })),
        { text: "P R E S E N T E", bold: true, fontSize: 10.5, margin: [0, 0, 0, 16] },
        ...parrafo(t.saludo),
        ...parrafo(t.objetivo),
        ...parrafo(t.resumen),
        ...parrafo(t.detalleRealizados),
        ...parrafo(t.detalleReportes),
        ...parrafo(t.cierre, { italics: true }),
        ...parrafo(t.despedida, { margin: [0, 4, 0, 0] }),
        {
            columns: [
                { width: "*", text: "" },
                {
                    width: 250,
                    stack: [
                        lineaFirma(46, 10),
                        { text: t.firmante || "", alignment: "center", bold: true, fontSize: 10 },
                        t.puesto ? { text: partible(t.puesto), alignment: "center", fontSize: 9, color: GRIS } : null,
                    ].filter(Boolean),
                },
                { width: "*", text: "" },
            ],
            unbreakable: true,
        },
    ];
};

const reporteVisitaCMMSPdf = (datos, textos) => {
    const t = textos || textosReporteVisita(datos);
    const dd = documentoCMMS({
        datos,
        content: [...carta(t), ...anexoA(datos, t)],
        pieIzquierda: `Reporte de visita · ${fechaCorta(datos.fecha)}`,
    });
    dd.defaultStyle = { fontSize: 10.5, color: "#1f2a37" };
    return dd;
};

export default reporteVisitaCMMSPdf;
